import axios from 'axios';
import {
  BostaDeliveryRequest,
  BostaDeliveryResponse,
  BostaTrackingResponse,
  BostaCity,
  BostaPickupLocation,
  BostaRateRequest,
  BostaRateResponse,
  BostaApiAddress,
  BostaDistrict,
  BostaZone
} from '../types/bosta';
import { BostaSettings } from '../types/storeSettings';

const BOSTA_API_BASE = 'https://app.bosta.co/api/v2';

const getApiKey = async (): Promise<string> => {
  const { getStoreSettings } = await import('./storeSettingsService');
  const settings = await getStoreSettings();
  if (!settings.bosta?.apiKey) {
    throw new Error('Bosta API key not configured. Please set it in store settings.');
  }
  return settings.bosta.apiKey;
};

const getConfig = async (): Promise<BostaSettings> => {
  const { getStoreSettings } = await import('./storeSettingsService');
  const settings = await getStoreSettings();
  if (!settings.bosta?.enabled) {
    throw new Error('Bosta shipping is not enabled');
  }
  return settings.bosta;
};

const bostaApi = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  data?: any,
  apiKey?: string
): Promise<T> => {
  const key = apiKey || await getApiKey();
  const response = await axios({
    method,
    url: `${BOSTA_API_BASE}${endpoint}`,
    data,
    headers: {
      'Authorization': key,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

const COUNTRY_ID_EGYPT = '60e4482c7cb7d4bc4849c4d5';

export const getBostaCities = async (): Promise<BostaCity[]> => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${BOSTA_API_BASE}/cities?countryId=${COUNTRY_ID_EGYPT}`,
      headers: {
        'Authorization': await getApiKey(),
        'Content-Type': 'application/json'
      }
    });

    const data = response.data;
    // Bosta returns: { success: true, data: { list: [...] } }
    const list = data?.data?.list || data?.data || data?.cities || data?.result;
    if (Array.isArray(list)) return list;
    return [];
  } catch (error: any) {
    console.error('Error fetching Bosta cities:', error?.response?.data || error?.message || error);
    return [];
  }
};

export const getBostaCityDistricts = async (cityId: string): Promise<BostaDistrict[]> => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${BOSTA_API_BASE}/cities/${cityId}/districts`,
      headers: {
        'Authorization': await getApiKey(),
        'Content-Type': 'application/json'
      }
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data.districts && Array.isArray(data.districts)) return data.districts;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error: any) {
    console.error('Error fetching Bosta districts:', error?.response?.data || error?.message || error);
    return [];
  }
};

export const getBostaCityZones = async (cityId: string): Promise<BostaZone[]> => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${BOSTA_API_BASE}/cities/${cityId}/zones`,
      headers: {
        'Authorization': await getApiKey(),
        'Content-Type': 'application/json'
      }
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data.zones && Array.isArray(data.zones)) return data.zones;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error: any) {
    console.error('Error fetching Bosta zones:', error?.response?.data || error?.message || error);
    return [];
  }
};

export const getAllBostaDistricts = async (): Promise<any[]> => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${BOSTA_API_BASE}/cities/getAllDistricts?countryId=${COUNTRY_ID_EGYPT}`,
      headers: {
        'Authorization': await getApiKey(),
        'Content-Type': 'application/json'
      }
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data.districts && Array.isArray(data.districts)) return data.districts;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error: any) {
    console.error('Error fetching all Bosta districts:', error?.response?.data || error?.message || error);
    return [];
  }
};

export const getBostaPickupLocations = async (): Promise<BostaPickupLocation[]> => {
  try {
    const data = await bostaApi<{ pickupLocations: BostaPickupLocation[] }>(
      'GET', '/business/pickup-locations'
    );
    return data.pickupLocations || [];
  } catch (error) {
    console.error('Error fetching Bosta pickup locations:', error);
    return [];
  }
};

export const calculateShippingRate = async (
  request: BostaRateRequest
): Promise<BostaRateResponse | null> => {
  try {
    const data = await bostaApi<BostaRateResponse>(
      'POST', '/deliveries/calculateRate', request
    );
    return data;
  } catch (error) {
    console.error('Error calculating Bosta rate:', error);
    return null;
  }
};

export const createBostaDelivery = async (
  request: BostaDeliveryRequest
): Promise<BostaDeliveryResponse> => {
  const data = await bostaApi<BostaDeliveryResponse>(
    'POST', `/deliveries?apiVersion=1`, request
  );
  return data;
};

export const trackBostaDelivery = async (
  deliveryId: string
): Promise<BostaTrackingResponse | null> => {
  try {
    const data = await bostaApi<BostaTrackingResponse>(
      'GET', `/deliveries/${deliveryId}/track`
    );
    return data;
  } catch (error) {
    console.error('Error tracking Bosta delivery:', error);
    return null;
  }
};

export const trackBostaByTrackingNumber = async (
  trackingNumber: string
): Promise<BostaTrackingResponse | null> => {
  try {
    const data = await bostaApi<BostaTrackingResponse>(
      'GET', `/deliveries/track/${trackingNumber}`
    );
    return data;
  } catch (error) {
    console.error('Error tracking Bosta delivery by number:', error);
    return null;
  }
};

export const getBostaDelivery = async (
  deliveryId: string
): Promise<BostaDeliveryResponse | null> => {
  try {
    const data = await bostaApi<BostaDeliveryResponse>(
      'GET', `/deliveries/${deliveryId}`
    );
    return data;
  } catch (error) {
    console.error('Error fetching Bosta delivery:', error);
    return null;
  }
};

export const cancelBostaDelivery = async (
  deliveryId: string
): Promise<boolean> => {
  try {
    await bostaApi('POST', `/deliveries/cancel/${deliveryId}`);
    return true;
  } catch (error) {
    console.error('Error cancelling Bosta delivery:', error);
    return false;
  }
};

export const getBostaWaybillUrl = async (
  deliveryIds: string[]
): Promise<string> => {
  const ids = deliveryIds.join(',');
  const data = await bostaApi<{ waybillUrl: string }>(
    'GET', `/deliveries/waybill?deliveriesIds=${ids}`
  );
  return data.waybillUrl;
};

export const createOrderBostaDelivery = async (
  orderId: string,
  config?: BostaSettings
): Promise<{ deliveryId: string; trackingNumber: number } | null> => {
  try {
    const bostaConfig = config || await getConfig();
    const { getOrderById } = await import('./storeService');
    const order = await getOrderById(orderId);

    if (!order) {
      throw new Error('Order not found');
    }

    const { shippingDetails, items, total, notes } = order;

    const firstName = shippingDetails.firstName || shippingDetails.address?.split(' ')[0] || order.customerName;
    const lastName = shippingDetails.lastName || '';

    const dropOffAddress: BostaApiAddress = {
      city: shippingDetails.bostaCityName || shippingDetails.city || 'القاهرة',
      firstLine: shippingDetails.address || shippingDetails.street || '',
      buildingNumber: shippingDetails.building,
      floor: shippingDetails.floor,
      apartment: shippingDetails.apartment,
      secondLine: notes || '',
      isWorkAddress: false
    };

    if (shippingDetails.bostaDistrictId) {
      dropOffAddress.districtId = shippingDetails.bostaDistrictId;
    } else if (shippingDetails.bostaDistrict) {
      dropOffAddress.districtName = shippingDetails.bostaDistrict;
      dropOffAddress.cityId = shippingDetails.bostaCityId;
    }

    if (shippingDetails.bostaZoneId) {
      dropOffAddress.zoneId = shippingDetails.bostaZoneId;
    }

    const request: BostaDeliveryRequest = {
      type: bostaConfig.deliveryType as '10' | '15',
      specs: {
        packageDetails: {
          description: `طلب رقم ${order.orderNumber}`,
          itemsCount: items.length
        },
        packageType: 'MEDIUM'
      },
      dropOffAddress,
      notes: notes,
      businessReference: order.orderNumber,
      webhookUrl: bostaConfig.webhookUrl || undefined,
      allowOpenPackage: false
    };

    if (bostaConfig.deliveryType === '15') {
      request.cod = total;
    }

    const response = await createBostaDelivery(request);

    const { updateDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../config/firebase');
    await updateDoc(doc(db, 'orders', orderId), {
      'bostaDeliveryId': response._id,
      'bostaTrackingNumber': response.trackingNumber,
      'bostaPricing': response.pricing,
      'bostaCreatedAt': response.createdAt,
      'bostaState': response.state,
      'bostaWaybillUrl': response.waybillUrl,
      'orderStatus': 'processing',
      'updatedAt': new Date()
    });

    return {
      deliveryId: response._id,
      trackingNumber: response.trackingNumber
    };
  } catch (error) {
    console.error('Error creating Bosta delivery for order:', error);
    return null;
  }
};

export const updateOrderBostaStatus = async (
  orderId: string,
  state: number,
  exceptionReason?: string
): Promise<void> => {
  const { updateDoc, doc } = await import('firebase/firestore');
  const { db } = await import('../config/firebase');
  const { BOSTA_STATE_MAP } = await import('../types/bosta');

  const stateName = BOSTA_STATE_MAP[state] || `الحالة ${state}`;

  const updateData: any = {
    'bostaState': state,
    'bostaStateName': stateName,
    'updatedAt': new Date()
  };

  if (exceptionReason) {
    updateData['bostaExceptionReason'] = exceptionReason;
  }

  if (state === 45) {
    updateData['orderStatus'] = 'delivered';
    updateData['deliveredAt'] = new Date();
  } else if (state === 49) {
    updateData['orderStatus'] = 'cancelled';
    updateData['cancelledAt'] = new Date();
  } else if (state === 41 || state === 21) {
    updateData['orderStatus'] = 'shipped';
    updateData['shippedAt'] = new Date();
  }

  await updateDoc(doc(db, 'orders', orderId), updateData);
};
